import classNames from 'classnames';
import { get } from 'lodash';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ENTITY_ID_SEARCH_PARAM_KEY,
  REGISTER_ID_SEARCH_PARAM_KEY,
} from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { queryClient } from '@/modules/common/query.ts';
import { conversionSourceAmount } from '@/modules/common/utils/brokerConversion.ts';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { CURRENT_USER_REGISTERS_QUERY_KEY } from '@/modules/investor-dashboard/queries/registers.tsx';
import { CURRENT_ENTITY_SHAREHOLDERS_QUERY_KEY } from '@/modules/investor-dashboard/queries/transactions.tsx';
import { fixNameEnum, goodFormatNumberCustom, goodFormatUSDCurrency } from 'ui/utils';
import { EventLogType, TransactionStatus } from 'api';
import { GetEntityShareholdersResponse, GetUserRegistersResponse } from 'api';

const formatEnumValue = (value?: string | null) =>
  fixNameEnum(value).replace(/unsuccessfull/gi, 'Unsuccessful');

const mapNotificationTypeToMarkupGenerator = {
  transaction: (
    jsonData: any,
    getRegisterTypeString: ReturnType<typeof useRegisterTypeStrings>,
  ) => {
    const transactionType = jsonData.type;
    const name =
      transactionType === 'subscription'
        ? getRegisterTypeString('application', { registerId: jsonData.register_id })
        : formatEnumValue(transactionType).toLowerCase();
    const isUseTotal =
      jsonData.type === 'drp' &&
      [
        TransactionStatus.DividendPending,
        TransactionStatus.Pending,
        TransactionStatus.Unsuccessfull,
      ].includes(jsonData.status);
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();

    return (
      <div>
        A{name[0] === 'a' ? 'n' : ''} {name} transaction of{' '}
        {!isUseTotal ? (
          <>
            <b>{Number(jsonData['quantity'] || 0).toFixed(2)}</b> quantity shares/units of class{' '}
          </>
        ) : (
          <>
            <b>{Number(jsonData['total'] || 0).toFixed(2)}</b> total{' '}
          </>
        )}
        {jsonData['register_class']
          ? jsonData['register_class']?.class_name
          : jsonData['register_class_id']}{' '}
        in registry{' '}
        {jsonData['register_obj'] ? jsonData['register_obj']?.name : jsonData['register_obj_id']}{' '}
        has been created with status{' '}
        <span className="font-[500] text-black text-[14px] tracking-wide leading-[24px]">
          {formatEnumValue(jsonData['status']).toLowerCase()}
        </span>
        {jsonData['shareholder'] ? (
          <span> for investor {jsonData['shareholder']?.entity?.entity_name}</span>
        ) : (
          <></>
        )}
        {'. '}
        {!isUseTotal && ` The price per share/unit is ${jsonData['price_per_share']}.`}
        {jsonData['issue_date']
          ? ` The transaction was issued on ${dateToPrint(jsonData['issue_date'])}.`
          : ''}
        {jsonData['memo'] ? ` Memo: ${jsonData['memo']}.` : ''}
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
      </div>
    );
  },
  transactionArchived: (
    jsonData: any,
    getRegisterTypeString: ReturnType<typeof useRegisterTypeStrings>,
  ) => {
    const transactionType = jsonData.type;
    const name =
      transactionType === 'subscription'
        ? getRegisterTypeString('application', { registerId: jsonData.register_id })
        : formatEnumValue(transactionType).toLowerCase();
    const isUseTotal =
      jsonData.type === 'drp' &&
      [
        TransactionStatus.DividendPending,
        TransactionStatus.Pending,
        TransactionStatus.Unsuccessfull,
      ].includes(jsonData.status);
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();
    const archivedAt = jsonData['archived_at'];

    return (
      <div>
        The {name} transaction of{' '}
        {!isUseTotal ? (
          <>
            <b>{Number(jsonData['quantity'] || 0).toFixed(2)}</b> quantity shares/units of class{' '}
          </>
        ) : (
          <>
            <b>{Number(jsonData['total'] || 0).toFixed(2)}</b> total{' '}
          </>
        )}
        {jsonData['register_class']
          ? jsonData['register_class']?.class_name
          : jsonData['register_class_id']}{' '}
        in registry{' '}
        {jsonData['register_obj'] ? jsonData['register_obj']?.name : jsonData['register_obj_id']}{' '}
        has been archived{archivedAt ? ` on ${dateToPrint(archivedAt)}` : ''}.
        {jsonData['shareholder'] ? (
          <span> Investor: {jsonData['shareholder']?.entity?.entity_name}.</span>
        ) : jsonData['shareholder_id'] ? (
          <span> Investor: {jsonData['shareholder_id']}.</span>
        ) : (
          <></>
        )}
        {jsonData['memo'] ? ` Memo: ${jsonData['memo']}.` : ''}
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
      </div>
    );
  },
  payment: (jsonData: any, getRegisterTypeString: ReturnType<typeof useRegisterTypeStrings>) => {
    const transactionType = jsonData['transaction']?.type;
    const transactionTypeUpdate =
      transactionType === 'subscription'
        ? getRegisterTypeString('application', { registerId: jsonData.register_id })
        : formatEnumValue(transactionType).toLowerCase();
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();
    return (
      <div>
        A payment of <b>${Number(jsonData['amount']).toFixed(2)}</b> has been made in the direction{' '}
        <b>{formatEnumValue(jsonData['direction']).toLowerCase()}</b> by investor{' '}
        {jsonData['shareholder'] && jsonData['shareholder']?.entity?.entity_name ? (
          <span>{jsonData['shareholder']?.entity?.entity_name} in registry</span>
        ) : jsonData['shareholder_id'] ? (
          <span>{jsonData['shareholder_id']} in registry</span>
        ) : (
          <></>
        )}
        {jsonData['register_obj']?.name ? (
          <span> {jsonData['register_obj']?.name}</span>
        ) : jsonData['register_id'] ? (
          <span> {jsonData['register_id']}</span>
        ) : (
          <></>
        )}{' '}
        with status{' '}
        <span className="font-[500] text-black text-[14px] tracking-wide leading-[24px]">
          {formatEnumValue(jsonData['status']).toLowerCase()}
        </span>
        {'. '}
        {jsonData['confirmation_date']
          ? ` The payment was confirmed on ${dateToPrint(jsonData['confirmation_date'])}. `
          : ''}
        The request was made on {dateToPrint(jsonData['requested_at_date'])}.
        {jsonData['transaction']?.type ? ` Transaction type is ${transactionTypeUpdate}.` : ''}
        {jsonData['dividend_event_id']
          ? ` Related to dividend type ${jsonData['dividend_event']?.type}.`
          : ''}
        {jsonData['memo'] ? ` Memo: ${jsonData['memo']}.` : ''}
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
      </div>
    );
  },
  entityUpdated: (jsonData: any) => {
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();
    return (
      <div>
        Entity <b>{jsonData['old_entity_name'] || 'Unnamed Entity'}</b> has been updated by{' '}
        {userName}.{jsonData['entity_name'] && ` Entity name: ${jsonData['entity_name']}.`}
        {jsonData['email'] && ` Contact email: ${jsonData['email']}.`}
        {jsonData['phone_number'] && ` Phone number: ${jsonData['phone_number']}.`}
        {jsonData['bank_account_name'] && ` Bank account name: ${jsonData['bank_account_name']}.`}
        {jsonData['bank_account_bsb'] && ` Bank account BSB: ${jsonData['bank_account_bsb']}.`}
        {jsonData['bank_account_number'] &&
          ` Bank account number: ${jsonData['bank_account_number']}.`}
        {jsonData['address'] && ` Address: ${jsonData['address']}.`}
        {jsonData['additional_emails'] &&
          ` Additional emails: ${
            Array.isArray(jsonData['additional_emails'])
              ? jsonData['additional_emails'].join(', ')
              : jsonData['additional_emails']
          }.`}
      </div>
    );
  },
  holderTaxReportByYear: (
    jsonData: Record<string, any>,
    registerName: string | null,
    holderName: string | null,
  ) => {
    const action =
      jsonData['action'] === 'created'
        ? 'created'
        : jsonData['action'] === 'updated'
        ? 'updated'
        : 'changed';
    const year = jsonData['year'];
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();

    return (
      <div>
        A holder tax report{year ? ` for year ${year}` : ''} has been {action}
        {holderName ? (
          <>
            {' '}
            for investor <b>{holderName}</b>
          </>
        ) : (
          ''
        )}
        {registerName ? <> in registry {registerName}</> : ''}.
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
      </div>
    );
  },
  registerTotalFundByYear: (jsonData: Record<string, any>, registerName: string | null) => {
    const action =
      jsonData['action'] === 'created'
        ? 'created'
        : jsonData['action'] === 'updated'
        ? 'updated'
        : 'changed';
    const year = jsonData['year'];
    const totalFund = jsonData['total_fund'];
    const previousTotalFund = jsonData['previous_total_fund'] ?? jsonData['old_total_fund'];
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();

    return (
      <div>
        A register total fund{year ? ` for year ${year}` : ''}{' '}
        {registerName ? `in registry ${registerName} ` : ''}
        has been {action}.
        {action === 'updated' &&
        previousTotalFund !== undefined &&
        previousTotalFund !== null &&
        totalFund !== undefined &&
        totalFund !== null
          ? ` Total fund amount changed from ${goodFormatUSDCurrency(
              Number(previousTotalFund),
            )} to ${goodFormatUSDCurrency(Number(totalFund))}.`
          : totalFund !== undefined && totalFund !== null
          ? ` Total fund amount is ${goodFormatUSDCurrency(Number(totalFund))}.`
          : ''}
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
      </div>
    );
  },
  entity: (jsonData: any) => {
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();
    return (
      <div>
        Entity <b>{jsonData['entity_name'] || 'Unnamed Entity'}</b> has been created with status{' '}
        <span className="font-[500] text-black text-[14px] tracking-wide leading-[24px]">
          {formatEnumValue(jsonData['status']).toLowerCase()}
        </span>
        .
        {jsonData['entity_application_rejection_reason']
          ? ` Reason for rejection: ${jsonData['entity_application_rejection_reason']}.`
          : ''}
        {jsonData['bank_account_name']
          ? ` Bank account name: ${jsonData['bank_account_name']}, BSB: ${jsonData['bank_account_bsb']}, account number: ${jsonData['bank_account_number']}.`
          : ''}
        {jsonData['email'] ? ` Contact email: ${jsonData['email']}.` : ''}
        {jsonData['phone_number'] ? ` Phone number: ${jsonData['phone_number']}.` : ''}
        {jsonData['address'] ? ` Address: ${jsonData['address']}.` : ''}
        {jsonData['additional_emails']
          ? ` Additional emails: ${
              Array.isArray(jsonData['additional_emails'])
                ? jsonData['additional_emails'].join(', ')
                : jsonData['additional_emails']
            }.`
          : ''}{' '}
        Preferred contact way is{' '}
        {jsonData['preferred_contact_way']
          ? formatEnumValue(jsonData['preferred_contact_way']).toLowerCase()
          : 'not specified'}
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
        {/*{jsonData['company_entity_details']*/}
        {/*  ? ` Company entity details: ${JSON.stringify(jsonData['company_entity_details'])}.`*/}
        {/*  : ''}*/}
        {/*{jsonData['trust_entity_details']*/}
        {/*  ? ` Trust entity details: ${JSON.stringify(jsonData['trust_entity_details'])}.`*/}
        {/*  : ''}*/}
        {/*{jsonData['partnership_entity_details']*/}
        {/*  ? ` Partnership entity details: ${JSON.stringify(*/}
        {/*      jsonData['partnership_entity_details'],*/}
        {/*    )}.`*/}
        {/*  : ''}*/}
        {/*{jsonData['association_entity_details_id']*/}
        {/*  ? ` Association entity details: ${JSON.stringify(*/}
        {/*      jsonData['association_entity_details_id'],*/}
        {/*    )}.`*/}
        {/*  : ''}*/}
        {/*{jsonData['registered_cooperative_investors_entity_details']*/}
        {/*  ? ` Registered cooperative investors entity details: ${JSON.stringify(*/}
        {/*      jsonData['registered_cooperative_investors_entity_details'],*/}
        {/*    )}.`*/}
        {/*  : ''}*/}
        {/*{jsonData['government_body_entity_details']*/}
        {/*  ? ` Government body entity details: ${JSON.stringify(*/}
        {/*      jsonData['government_body_entity_details'],*/}
        {/*    )}.`*/}
        {/*  : ''}*/}
      </div>
    );
  },
  transfer: (jsonData: any) => {
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();
    return (
      <div>
        Transfer of <b>{Number(jsonData['quantity']).toFixed(2)}</b> quantity units of class{' '}
        {jsonData['register_class']
          ? jsonData['register_class']?.class_name
          : jsonData['register_class_id']}{' '}
        in registry{' '}
        {jsonData['register_obj'] ? jsonData['register_obj']?.name : jsonData['register_obj_id']}{' '}
        from investor{' '}
        <b>
          {jsonData['from_shareholder']
            ? jsonData['from_shareholder']?.entity?.entity_name
            : jsonData['from_shareholder_id']}
        </b>{' '}
        to investor{' '}
        <b>
          {jsonData['to_shareholder']
            ? jsonData['to_shareholder']?.entity?.entity_name
            : jsonData['to_shareholder_id']}
        </b>{' '}
        has been created with status{' '}
        <span className="font-[500] text-black text-[14px] tracking-wide leading-[24px]">
          "{formatEnumValue(jsonData['status']).toLowerCase()}"
        </span>
        . The price per share/unit is <b>${jsonData['price_per_share']}</b>.
        {jsonData['confirmation_date']
          ? ` The transfer was confirmed on ${dateToPrint(jsonData['confirmation_date'])}.`
          : ''}
        {jsonData['requested_at_date']
          ? ` The transfer was requested on ${dateToPrint(jsonData['requested_at_date'])}.`
          : ''}
        {jsonData['memo'] ? ` Memo: ${jsonData['memo']}.` : ''}
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
      </div>
    );
  },
  transferArchived: (jsonData: any) => {
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();
    const archivedAt = jsonData['archived_at'];

    return (
      <div>
        Transfer of <b>{Number(jsonData['quantity']).toFixed(2)}</b> quantity units of class{' '}
        {jsonData['register_class']
          ? jsonData['register_class']?.class_name
          : jsonData['register_class_id']}{' '}
        in registry{' '}
        {jsonData['register_obj'] ? jsonData['register_obj']?.name : jsonData['register_obj_id']}{' '}
        from investor{' '}
        <b>
          {jsonData['from_shareholder']
            ? jsonData['from_shareholder']?.entity?.entity_name
            : jsonData['from_shareholder_id']}
        </b>{' '}
        to investor{' '}
        <b>
          {jsonData['to_shareholder']
            ? jsonData['to_shareholder']?.entity?.entity_name
            : jsonData['to_shareholder_id']}
        </b>{' '}
        has been archived{archivedAt ? ` on ${dateToPrint(archivedAt)}` : ''}.
        {jsonData['memo'] ? ` Memo: ${jsonData['memo']}.` : ''}
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
      </div>
    );
  },
  nav: (jsonData: any, type: EventLogType) => {
    const action = type.toLowerCase().includes('created')
      ? 'Created'
      : type.toLowerCase().includes('updated')
      ? 'Updated'
      : 'Deleted';
    const isActive = jsonData?.['is_active'] ?? false;
    const isHistorical = jsonData?.['is_historical'] ?? false;
    const isFuture = jsonData?.['is_future'] ?? false;

    const prefix = isActive ? 'Active' : isHistorical ? 'Historical' : isFuture ? 'Future' : 'The';

    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();
    return (
      <div>
        {action === 'Deleted' ? (
          <>
            {prefix} NAV for the {jsonData['register_class_name']} security class, with a price per
            share/unit of <b>{Number(jsonData['price_per_share']).toFixed(2)}</b>, was deleted on{' '}
            {dateToPrint(jsonData['changes_date'])}.
            {jsonData['justification'] && (
              <>
                <br />
                Justification: {jsonData['justification']}
              </>
            )}
          </>
        ) : (
          <>
            {prefix} NAV for the {jsonData['register_class_name']} security class was{' '}
            {action.toLowerCase()} to a price per share/unit of{' '}
            <b>{Number(jsonData['price_per_share']).toFixed(2)}</b> on{' '}
            {dateToPrint(jsonData['changes_date'])}.
            {jsonData['justification'] && (
              <>
                <br />
                Justification: {jsonData['justification']}
              </>
            )}
          </>
        )}
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
      </div>
    );
  },
  communication: (jsonData: any, isIssuer?: boolean) => {
    const entity = jsonData['entity'];
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();
    if (isIssuer) {
      return (
        <div>
          <>
            A new announcement has been sent to {entity['entity_name']} on{' '}
            {dateToPrint(jsonData['date'])}. The message was: {jsonData['subject']}
          </>
          {userName && (
            <>
              <br />
              Action initiated by: {userName}.
            </>
          )}
        </div>
      );
    } else {
      return (
        <div>
          <>
            You have received a new announcement on {dateToPrint(jsonData['date'])}. The message
            reads: {jsonData['subject']}. For more details, please check your inbox.
          </>
          {userName && (
            <>
              <br />
              Action initiated by: {userName}.
            </>
          )}
        </div>
      );
    }
  },
  document: (jsonData: Record<string, any>) => {
    const isTransfer = !!get(jsonData, 'transfer_id');
    const operationId = isTransfer ? get(jsonData, 'transfer_id') : get(jsonData, 'transaction_id');
    const document_update_type = get(jsonData, 'document_update_type');

    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();

    return (
      <div>
        {isTransfer ? 'Transfer' : 'Transaction'} #{operationId} document has been{' '}
        {document_update_type}d{userName && <span> by {userName}</span>}
      </div>
    );
  },
  custodyAudit: (jsonData: Record<string, any>) => {
    const after = jsonData.after as Record<string, unknown> | null;
    const before = jsonData.before as Record<string, unknown> | null;
    const changedFields = Array.isArray(jsonData.changed_fields)
      ? (jsonData.changed_fields as string[])
      : [];
    const user = jsonData.user as Record<string, string | null> | null;
    const userName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    const actor =
      jsonData.actor_type === 'user'
        ? userName || user?.email || 'User'
        : jsonData.actor_type === 'ai'
        ? 'AI'
        : 'System';
    const formatValue = (value: unknown) =>
      value === null || value === undefined
        ? '—'
        : typeof value === 'boolean'
        ? value
          ? 'Yes'
          : 'No'
        : String(value);
    const description =
      after?.name ||
      after?.bank_name ||
      after?.transaction_type ||
      after?.direction ||
      after?.ticker;
    const isConversion =
      jsonData.entity_type === 'portfolio_transaction' &&
      after?.from_currency &&
      after?.to_currency &&
      (after?.transaction_type === 'broker_deposit' ||
        after?.transaction_type === 'broker_withdraw');
    const isConversionWithdraw = isConversion && after?.transaction_type === 'broker_withdraw';
    const conversionAmount = isConversionWithdraw
      ? conversionSourceAmount(
          after?.amount as string | number | null,
          after?.fx_rate as string | number | null,
        )
      : Number(after?.amount);
    const conversionCurrency = isConversionWithdraw ? after?.from_currency : after?.to_currency;
    const isRevaluation =
      jsonData.entity_type === 'portfolio_transaction' && after?.transaction_type === 'revaluation';
    const isTrade =
      jsonData.entity_type === 'portfolio_transaction' &&
      ['trade_buy_book', 'trade_buy_settle', 'trade_sell_book', 'trade_sell_settle'].includes(
        String(after?.transaction_type),
      );
    const tradeCurrency = after?.currency || before?.currency;
    const tradeAmount = Number(after?.price_per_share) * Number(after?.quantity);

    return (
      <div>
        {formatEnumValue(jsonData.entity_type)} #{jsonData.entity_id} has been {jsonData.action}
        {isConversion
          ? ` (Broker Currency Conversion · ${isConversionWithdraw ? 'Withdrawal' : 'Deposit'})`
          : description
          ? ` (${formatEnumValue(String(description))})`
          : ''}
        {jsonData.register_name ? ` in register ${jsonData.register_name}` : ''} by {actor}.
        {isConversion &&
        conversionAmount != null &&
        Number.isFinite(conversionAmount) &&
        conversionCurrency
          ? ` Amount: ${goodFormatNumberCustom(
              conversionAmount,
              String(conversionCurrency).toUpperCase(),
              2,
              2,
            )}. Pair: ${String(after?.from_currency).toUpperCase()} → ${String(
              after?.to_currency,
            ).toUpperCase()}.`
          : isTrade &&
            after?.price_per_share != null &&
            after?.quantity != null &&
            Number.isFinite(tradeAmount)
          ? ` Amount: ${goodFormatNumberCustom(
              tradeAmount,
              tradeCurrency ? String(tradeCurrency).toUpperCase() : '',
              2,
              2,
            )}.`
          : after?.amount != null &&
            ` Amount: ${goodFormatNumberCustom(
              Number(after.amount),
              isRevaluation && after?.currency ? String(after.currency).toUpperCase() : '',
              2,
              2,
            )}.`}
        {isTrade &&
          after?.quantity != null &&
          ` Quantity: ${goodFormatNumberCustom(Number(after.quantity), '', 2, 2)}.`}
        {isTrade &&
          after?.price_per_share != null &&
          ` Price per share: ${goodFormatNumberCustom(
            Number(after.price_per_share),
            tradeCurrency ? String(tradeCurrency).toUpperCase() : '',
            2,
            6,
          )}.`}
        {isRevaluation &&
          after?.price_per_share != null &&
          ` Price per share: ${goodFormatNumberCustom(
            Number(after.price_per_share),
            after?.currency ? String(after.currency).toUpperCase() : '',
            2,
            6,
          )}.`}
        {jsonData.parent_entity_type && jsonData.parent_entity_id && (
          <>
            {' '}
            Related to {formatEnumValue(jsonData.parent_entity_type)} #{jsonData.parent_entity_id}.
          </>
        )}
        {jsonData.change_reason && <> Reason: {jsonData.change_reason}.</>}
        {changedFields.length > 0 && (
          <div>
            Changes:{' '}
            {changedFields.map((field, index) => (
              <span key={field}>
                {index > 0 && '; '}
                {field === 'account_details' ? (
                  <>Account details changed</>
                ) : (
                  <>
                    {formatEnumValue(field)}: {formatValue(before?.[field])} →{' '}
                    {formatValue(after?.[field])}
                  </>
                )}
              </span>
            ))}
            .
          </div>
        )}
      </div>
    );
  },
  custodyEmailReprocessing: (jsonData: Record<string, any>) => {
    const user = jsonData.user as Record<string, string | null> | null;
    const userName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    const actor = userName || user?.email || 'User';
    const senderName = jsonData.sender_name ? `${jsonData.sender_name} ` : '';
    const senderEmail = jsonData.sender_email ? `<${jsonData.sender_email}>` : 'unknown sender';
    const subject = jsonData.subject || 'No subject';

    return (
      <div>
        Email "{subject}" from {senderName}
        {senderEmail} was queued for AI reprocessing by {actor}
        {user?.email && user.email !== actor ? ` <${user.email}>` : ''}.
      </div>
    );
  },
  dividend: (
    jsonData: Record<string, any>,
    getRegisterTypeString: ReturnType<typeof useRegisterTypeStrings>,
  ) => {
    const status = get(jsonData, 'status');
    const dividendObj = get(jsonData, 'dividend');
    const isPending = status === 'pending';
    const dividendId = get(jsonData, 'dividend_event_id');

    const dividend = getRegisterTypeString('Dividend', {
      registerId: jsonData.register_id,
    });
    const name = get(jsonData, 'label');
    const price = get(jsonData, 'amount_per_share');

    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();

    return (
      <div>
        A {dividend} #{dividendId} "{name}" has been {isPending ? 'created' : 'completed'} with
        price per share {goodFormatUSDCurrency(price)}
        <span>
          {' '}
          by {userName} on {dateToPrint(dividendObj['updated_at'])}
        </span>
      </div>
    );
  },
  dividendArchived: (
    jsonData: Record<string, any>,
    getRegisterTypeString: ReturnType<typeof useRegisterTypeStrings>,
  ) => {
    const dividend = getRegisterTypeString('Dividend', {
      registerId: jsonData.register_id,
    });
    const name = get(jsonData, 'label');
    const dividendId = get(jsonData, 'dividend_event_id');
    const price = get(jsonData, 'amount_per_share');
    const archivedAt = get(jsonData, 'archived_at');
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();

    return (
      <div>
        The {dividend} #{dividendId} "{name}" has been archived
        {archivedAt ? ` on ${dateToPrint(archivedAt)}` : ''}.
        {price ? ` Price per share was ${goodFormatUSDCurrency(price)}.` : ''}
        {jsonData['memo'] ? ` Memo: ${jsonData['memo']}.` : ''}
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
      </div>
    );
  },
  correspondence: (jsonData: Record<string, any>) => {
    const sender_email = get(jsonData, 'correspondence.sender_email');
    const ai_extracted = get(jsonData, 'correspondence.ai_extracted');
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();
    return (
      <div>
        {sender_email} sent massage: "{ai_extracted}"{' '}
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
      </div>
    );
  },
  buybackArchived: (jsonData: Record<string, any>) => {
    const registerClass =
      jsonData['register_class']?.class_name || jsonData['register_class_id'] || 'unknown class';
    const registerName = jsonData['register_obj']?.name || jsonData['register_id'] || '';
    const archivedAt = jsonData['archived_at'];
    const totalOffer = goodFormatUSDCurrency(Number(jsonData['total_buy_back_offer'] ?? 0));
    const pricePerShare = Number(jsonData['price_per_share'] ?? 0);
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();

    return (
      <div>
        The buyback event for class {registerClass}
        {registerName ? <> in registry {registerName}</> : ''} has been archived
        {archivedAt ? ` on ${dateToPrint(archivedAt)}` : ''}. The total offer was {totalOffer}
        {pricePerShare ? ` at ${goodFormatUSDCurrency(pricePerShare)} per share.` : '.'}
        {jsonData['memo'] ? ` Memo: ${jsonData['memo']}.` : ''}
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
      </div>
    );
  },
  holderEditRequestCreated: (jsonData: Record<string, any>) => {
    const holderName =
      jsonData['shareholder']?.entity?.entity_name ||
      jsonData['shareholder']?.entity_name ||
      jsonData['entity']?.entity_name ||
      jsonData['entity_name'] ||
      jsonData['shareholder_id'] ||
      'holder';
    const registerName = jsonData['register_obj']?.name || jsonData['register_id'];
    const requestId =
      jsonData['holder_edit_request_id'] || jsonData['investor_edit_id'] || jsonData['id'];
    const description = jsonData['description'];
    const requestedAt = jsonData['requested_at'];
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();

    return (
      <div>
        Holder edit request{requestId ? ` #${requestId}` : ''} for investor <b>{holderName}</b>
        {registerName ? <> in registry {registerName}</> : ''} has been submitted
        {requestedAt ? ` on ${dateToPrint(requestedAt)}` : ''} and is awaiting review.
        {description ? ` Notes: ${description}.` : ''}
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
      </div>
    );
  },
  holderEditRequestApproved: (jsonData: Record<string, any>) => {
    const holderName =
      jsonData['shareholder']?.entity?.entity_name ||
      jsonData['shareholder']?.entity_name ||
      jsonData['entity']?.entity_name ||
      jsonData['entity_name'] ||
      jsonData['shareholder_id'] ||
      'holder';
    const registerName = jsonData['register_obj']?.name || jsonData['register_id'];
    const requestId =
      jsonData['holder_edit_request_id'] || jsonData['investor_edit_id'] || jsonData['id'];
    const description = jsonData['description'];
    const approvedAt = jsonData['approved_at'] || jsonData['updated_at'];
    const userName = `${jsonData['user']?.first_name ?? ''} ${jsonData['user']?.last_name ?? ''}${
      jsonData['user']?.email ? ' <' + jsonData['user']?.email + '>' : ''
    }`.trim();

    return (
      <div>
        Holder edit request{requestId ? ` #${requestId}` : ''} for investor <b>{holderName}</b>
        {registerName ? <> in registry {registerName}</> : ''} has been approved
        {approvedAt ? ` on ${dateToPrint(approvedAt)}` : ''}. The holder profile now reflects the
        submitted changes.
        {description ? ` Notes: ${description}.` : ''}
        {userName && (
          <>
            <br />
            Action initiated by: {userName}.
          </>
        )}
      </div>
    );
  },
  investorAddedFromRegister: (jsonData: any) => {
    const { entity_name, entity_type, source_register_name, target_register_name, issuer_name } =
      jsonData;

    return (
      <div>
        Investor{' '}
        <b>
          {entity_name}
          {entity_type ? ` (${entity_type})` : ''}
        </b>{' '}
        was added from registry <b>{source_register_name}</b> to registry{' '}
        <b>{target_register_name}</b>.
        {issuer_name && (
          <>
            <br />
            Action initiated by: {issuer_name}.
          </>
        )}
      </div>
    );
  },
};

export const useBuildNotificationFromJson = (
  type: EventLogType,
  data: object,
  truncate: boolean,
  isIssuer?: boolean,
) => {
  const [params] = useSearchParams();
  const getRegisterTypeString = useRegisterTypeStrings();
  const currentEntityId = +(params.get(ENTITY_ID_SEARCH_PARAM_KEY) || 0);
  const selectedRegisterId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || 0);

  const resolveRegisterName = (jsonData: Record<string, any>) => {
    const payloadName =
      jsonData['register_obj']?.name || jsonData['register']?.name || jsonData['register_name'];
    if (payloadName) return payloadName;

    const registerId = +(jsonData['register_id'] || selectedRegisterId || 0);
    if (!registerId) return null;

    const registersData = queryClient.getQueryData<GetUserRegistersResponse>([
      CURRENT_USER_REGISTERS_QUERY_KEY,
    ]);
    const register = (registersData?.data || []).find(item => item.register_id === registerId);
    return register?.register_obj?.name || null;
  };

  const resolveHolderName = (jsonData: Record<string, any>) => {
    const payloadName =
      jsonData['shareholder']?.entity?.entity_name ||
      jsonData['shareholder']?.entity_name ||
      jsonData['entity']?.entity_name ||
      jsonData['entity_name'];
    if (payloadName) return payloadName;

    const holderId = +(jsonData['shareholder_id'] || jsonData['entity_id'] || 0);
    if (!holderId || !currentEntityId) return null;

    const shareholdersData = queryClient.getQueryData<GetEntityShareholdersResponse>([
      CURRENT_ENTITY_SHAREHOLDERS_QUERY_KEY,
      currentEntityId,
    ]);
    const shareholder = (shareholdersData?.shareholders || []).find(item => item.id === holderId);
    return shareholder?.entity?.entity_name || null;
  };

  const text = useMemo(() => {
    switch (type) {
      case EventLogType.TransactionApproval:
      case EventLogType.TransactionAwaitingPayment:
      case EventLogType.TransactionCreated:
      case EventLogType.TransactionExpired:
      case EventLogType.TransactionUnsuccessfull:
      case EventLogType.TransactionDividendPending:
        return mapNotificationTypeToMarkupGenerator['transaction'](data, getRegisterTypeString);
      case EventLogType.TransactionArchived:
        return mapNotificationTypeToMarkupGenerator['transactionArchived'](
          data,
          getRegisterTypeString,
        );

      case EventLogType.PaymentApproval:
      case EventLogType.PaymentCreated:
      case EventLogType.PaymentUnsuccessfull:
        return mapNotificationTypeToMarkupGenerator['payment'](data, getRegisterTypeString);

      case EventLogType.EntityUpdated:
        return mapNotificationTypeToMarkupGenerator['entityUpdated'](data);
      case EventLogType.HolderTaxReportByYearCreated:
      case EventLogType.HolderTaxReportByYearUpdated:
        return mapNotificationTypeToMarkupGenerator.holderTaxReportByYear(
          data,
          resolveRegisterName(data as Record<string, any>),
          resolveHolderName(data as Record<string, any>),
        );
      case EventLogType.RegisterTotalFundByYearCreated:
      case EventLogType.RegisterTotalFundByYearUpdated:
        return mapNotificationTypeToMarkupGenerator.registerTotalFundByYear(
          data,
          resolveRegisterName(data as Record<string, any>),
        );

      case EventLogType.DocumentCreated:
      case EventLogType.DocumentDeleted:
        return mapNotificationTypeToMarkupGenerator['document'](data);

      case EventLogType.CustodyPortfolioTransactionCreated:
      case EventLogType.CustodyPortfolioTransactionUpdated:
      case EventLogType.CustodyPortfolioTransactionArchived:
      case EventLogType.CustodyPortfolioTransactionRestored:
      case EventLogType.CustodyPortfolioTransactionCancelled:
      case EventLogType.CustodyCashMovementCreated:
      case EventLogType.CustodyCashMovementUpdated:
      case EventLogType.CustodyCashMovementArchived:
      case EventLogType.CustodyCashMovementRestored:
      case EventLogType.CustodyAssetCreated:
      case EventLogType.CustodyAssetUpdated:
      case EventLogType.CustodyAssetArchived:
      case EventLogType.CustodyAssetRestored:
      case EventLogType.CustodyBankCreated:
      case EventLogType.CustodyBankUpdated:
      case EventLogType.CustodyBankArchived:
      case EventLogType.CustodyBankRestored:
      case EventLogType.CustodyBrokerCreated:
      case EventLogType.CustodyBrokerUpdated:
      case EventLogType.CustodyBrokerArchived:
      case EventLogType.CustodyBrokerRestored:
        return mapNotificationTypeToMarkupGenerator.custodyAudit(data);
      case EventLogType.CustodyEmailReprocessingRequested:
        return mapNotificationTypeToMarkupGenerator.custodyEmailReprocessing(data);

      case EventLogType.DividendCreated:
      case EventLogType.DividendFinalized:
        return mapNotificationTypeToMarkupGenerator['dividend'](data, getRegisterTypeString);
      case EventLogType.DividendArchived:
        return mapNotificationTypeToMarkupGenerator['dividendArchived'](
          data,
          getRegisterTypeString,
        );

      case EventLogType.EntityAwaitedApproval:
      case EventLogType.EntityCreated:
      case EventLogType.EntityUnsuccessfull:
      case EventLogType.EntityApproval:
        return mapNotificationTypeToMarkupGenerator['entity'](data);
      case EventLogType.TransferApproval:
      case EventLogType.TransferCreated:
      case EventLogType.TransferUnsuccessfull:
        return mapNotificationTypeToMarkupGenerator['transfer'](data);
      case EventLogType.TransferArchived:
        return mapNotificationTypeToMarkupGenerator['transferArchived'](data);

      case EventLogType.NavCreated:
      case EventLogType.NavDeleted:
      case EventLogType.NavUpdated:
        return mapNotificationTypeToMarkupGenerator['nav'](data, type);

      case EventLogType.CommunicationCreated:
        return mapNotificationTypeToMarkupGenerator['communication'](data, isIssuer);
      case EventLogType.CorrespondenceCreated:
      case EventLogType.CorrespondenceReplyCreated:
        return mapNotificationTypeToMarkupGenerator.correspondence(data);
      case EventLogType.BuybackArchived:
        return mapNotificationTypeToMarkupGenerator.buybackArchived(data);
      case EventLogType.HolderEditRequestCreated:
        return mapNotificationTypeToMarkupGenerator.holderEditRequestCreated(data);
      case EventLogType.HolderEditRequestApproved:
        return mapNotificationTypeToMarkupGenerator.holderEditRequestApproved(data);
      case EventLogType.InvestorAddedFromRegister:
        return mapNotificationTypeToMarkupGenerator.investorAddedFromRegister(data);

      default:
        return '';
    }
  }, [currentEntityId, data, getRegisterTypeString, isIssuer, selectedRegisterId, type]);

  return <div className={classNames({ '[&>*]:truncate': truncate })}>{text}</div>;
};
